import type { SingleBillInfoFormValues, SingleBillInfoFormData } from '@ctct/application-layer';
import { Heading } from '@ctct/payflow-ui';
import { FormFieldCheckbox, FormFieldNonFekit, FormFieldSelectNonFekit } from '@ctct/presentation-layer';

import type { SingleBillInfoFormCopy } from './types';

interface Props {
  isEditing?: boolean;
  copy: SingleBillInfoFormCopy;
  countriesOptions?: ReturnType<SingleBillInfoFormData['getCountries']>;
  dayOptions: ReturnType<SingleBillInfoFormData['getDayOfMonthOptions']>;
  currrencyOptions: ReturnType<SingleBillInfoFormData['getCurrencyOptions']>;
  isShowingStates?: boolean;
  stateLabel?: ReturnType<SingleBillInfoFormData['getStateLabel']>;
  statePlaceholder?: ReturnType<SingleBillInfoFormData['getStatePlaceholder']>;
  states?: ReturnType<SingleBillInfoFormData['getStates']>;
  disableCountry?: boolean;
  // vat
  showTaxNumber: boolean;
  taxDisclaimer: string;
  taxLabel: string;
}

export const SingleBillInfoForm = ({
  isEditing = false,
  copy,
  countriesOptions = [],
  dayOptions = [],
  currrencyOptions = [],
  isShowingStates = false,
  stateLabel = '',
  statePlaceholder,
  states = [],
  disableCountry = false,
  showTaxNumber,
  taxDisclaimer,
  taxLabel,
}: Props) => {
  return (
    <div className="grid-form-container">
      <div className="columns-container">
        <FormFieldSelectNonFekit<SingleBillInfoFormValues>
          name="billingDayOfMonth"
          qeId="field-billing-day-of-month"
          label={copy.billingDayOfMonth}
          items={dayOptions}
          disabled={isEditing}
          version="v2"
        />
        <FormFieldSelectNonFekit<SingleBillInfoFormValues>
          name="currency"
          qeId="field-currency"
          label={copy.currency}
          items={currrencyOptions}
          disabled={isEditing}
          version="v2"
        />
      </div>
      <FormFieldNonFekit<SingleBillInfoFormValues>
        name="companyname"
        qeId="field-companyname"
        label={copy.companyName}
        version="v2"
      />
      <div className="columns-container">
        <FormFieldNonFekit<SingleBillInfoFormValues>
          name="firstname"
          qeId="field-fname"
          label={copy.firstName}
          version="v2"
        />
        <FormFieldNonFekit<SingleBillInfoFormValues>
          name="lastname"
          qeId="field-lname"
          label={copy.lastName}
          version="v2"
        />
      </div>
      <FormFieldSelectNonFekit<SingleBillInfoFormValues>
        name="country"
        qeId="field-countries"
        label={copy.country}
        items={countriesOptions}
        disabled={disableCountry}
        version="v2"
      />
      <FormFieldNonFekit<SingleBillInfoFormValues>
        name="address1"
        qeId="field-streetAddress1"
        label={copy.addressLine1}
        version="v2"
      />
      <FormFieldNonFekit<SingleBillInfoFormValues>
        name="address2"
        qeId="field-streetAddress2"
        label={copy.addressLine2}
        optional
        version="v2"
      />
      <div className="columns-container">
        <FormFieldNonFekit<SingleBillInfoFormValues> name="city" qeId="field-city" label={copy.city} version="v2" />

        {isShowingStates && (
          <FormFieldSelectNonFekit<SingleBillInfoFormValues>
            name="state"
            qeId="field-state"
            label={stateLabel ?? copy.state}
            placeholder={statePlaceholder ?? copy.selectState}
            allowEmpty
            items={states}
            version="v2"
          />
        )}

        <FormFieldNonFekit<SingleBillInfoFormValues>
          name="zipcode"
          qeId="field-zipcode"
          label={copy.zipcode}
          version="v2"
        />
      </div>

      {showTaxNumber && (
        <>
          <FormFieldNonFekit<SingleBillInfoFormValues>
            name="vatNumber"
            qeId="field-vat-number"
            label={taxLabel}
            disableMarginBottom
            optional
            version="v2"
          />
          <FormFieldCheckbox<SingleBillInfoFormValues>
            name="currentTaxAgreement"
            qeId="field-tax-agreement"
            label={taxDisclaimer}
            version="v2"
          />
        </>
      )}

      <FormFieldNonFekit<SingleBillInfoFormValues> name="email" qeId="field-email" label={copy.email} version="v2" />
      <FormFieldNonFekit<SingleBillInfoFormValues> name="phone" qeId="field-phone" label={copy.phone} version="v2" />

      <div className="single-bill-info__additional-info">
        <Heading level="h6-xs">{copy.additionalInfoHeader} </Heading>
        <p>{copy.additionalInfoText}</p>
      </div>
      <FormFieldNonFekit<SingleBillInfoFormValues>
        name="partnerContactName"
        qeId="field-partner-contact-name"
        label={copy.partnerContactName}
        version="v2"
      />
      <FormFieldNonFekit<SingleBillInfoFormValues>
        name="partnerContactEmail"
        qeId="field-partner-contact-email"
        label={copy.partnerContactEmail}
        version="v2"
      />
    </div>
  );
};
